/**
 * screens-mapping.ts — ตั้งค่า mapping และการปันส่วน (T098)
 *
 * - ผูก fee code / tax type / หมวดย่อย / SKU / ช่องทาง เข้าหัวข้อ P&L (แก้ได้โดยไม่ deploy)
 * - template คอลัมน์ไฟล์ (ใช้ซ้ำตอนนำเข้า)
 * - กติกาการปันส่วนแบบมีเวอร์ชัน + สั่งปันส่วนและดูผลย้อนหลัง
 */
import { MAPPABLE_PNL_CATEGORIES } from '@rrp/shared';
import type { Page } from './router.js';
import { getTenant } from './state.js';
import * as api from './api.js';
import {
  esc,
  money,
  pageShell,
  dataTable,
  pill,
  val,
  bindSubmit,
  errorBox,
  drawerHtml,
  promptDialog,
} from './ui.js';

type Row = Record<string, unknown>;

const SOURCE_KIND_LABELS: Record<string, string> = {
  fee_code: 'รหัสค่าธรรมเนียมจากแพลตฟอร์ม',
  tax_type: 'ประเภทภาษี',
  subcategory: 'หมวดย่อยในไฟล์',
  sku: 'SKU',
  channel: 'ช่องทางขาย',
};

const CATEGORY_OPTIONS = MAPPABLE_PNL_CATEGORIES.map(
  (c) => `<option value="${esc(c.label)}">${esc(c.label)}</option>`,
).join('');

export const mappingPage: Page = {
  title: 'ตั้งค่าการจับคู่ (Mapping) และการปันส่วน',
  render: () =>
    pageShell(
      'ตั้งค่า mapping และการปันส่วน',
      `<section class="card">
        <h3>ผูกค่าจากต้นทาง → หัวข้อ P&L</h3>
        <p class="foot-note">แก้ได้ทันทีโดยไม่ต้อง deploy — ใช้กับค่าธรรมเนียมแพลตฟอร์ม ประเภทภาษี หมวดย่อยในไฟล์ SKU และช่องทาง</p>
        <form id="pnl-map-form" class="grid-form">
          <label>ชนิดของค่า<select name="sourceKind">${Object.entries(SOURCE_KIND_LABELS)
            .map(([v, l]) => `<option value="${v}">${esc(l)}</option>`)
            .join('')}</select></label>
          <label>ค่าจากต้นทาง<input name="sourceValue" placeholder="เช่น commission_fee หรือ ค่ากล่อง" required></label>
          <label>หัวข้อ P&L<select name="pnlCategory">${CATEGORY_OPTIONS}</select></label>
          <div class="form-actions"><button type="submit">บันทึก mapping</button><span id="pnl-map-msg" class="muted"></span></div>
        </form>
        <div id="pnl-map-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>Template คอลัมน์ไฟล์นำเข้า</h3>
        <p class="foot-note">บันทึกการจับคู่คอลัมน์ที่ใช้ประจำ เช่น ไฟล์รายจ่ายจากฝ่ายบัญชี — รูปแบบ <code>ชื่อคอลัมน์ในไฟล์=target</code> บรรทัดละคู่</p>
        <form id="tpl-form" class="grid-form">
          <label>ชื่อ template<input name="templateName" placeholder="ไฟล์รายจ่ายรายเดือน" required></label>
          <label>แหล่งข้อมูล<select name="source">
            <option value="csv">CSV</option><option value="xlsx">Excel</option><option value="manual">กรอกเอง</option>
          </select></label>
          <label style="grid-column:1/-1">การจับคู่คอลัมน์
            <textarea name="columnMap" rows="5" placeholder="เลขที่=external_id&#10;วันที่=event_date&#10;ยอดรวม=total_amount" required></textarea></label>
          <div class="form-actions"><button type="submit">บันทึก template</button><span id="tpl-msg" class="muted"></span></div>
        </form>
        <div id="tpl-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>ผูกรหัสสินค้าจากช่องทาง → SKU (alias)</h3>
        <p class="foot-note">ชื่อสินค้าที่เปลี่ยนข้ามเดือนให้ผูกเป็น alias — ห้ามรวมชื่อเองโดยเดา (CONTEXT §8)</p>
        <form id="alias-form" class="grid-form">
          <label>SKU<select name="skuId" id="alias-sku"><option value="">กำลังโหลด…</option></select></label>
          <label>ช่องทาง<select name="channelCode">
            <option value="tiktok_shop">TikTok Shop</option><option value="shopee">Shopee</option>
            <option value="lazada">Lazada</option><option value="facebook">Facebook</option><option value="line">LINE</option>
          </select></label>
          <label>ร้าน<select name="shopId" id="alias-shop"><option value="">กำลังโหลด…</option></select></label>
          <label>รหัสจากช่องทาง<input name="externalSkuId" placeholder="TT-123456" required></label>
          <label>ชื่อจากช่องทาง<input name="externalName" placeholder="ชื่อที่แสดงในแพลตฟอร์ม"></label>
          <div class="form-actions"><button type="submit">ผูกรหัส</button><span id="alias-msg" class="muted"></span></div>
        </form>
        <div id="alias-list">กำลังโหลด…</div>
      </section>

      <section class="card">
        <h3>กติกาการปันส่วนค่าใช้จ่ายส่วนกลาง</h3>
        <p class="foot-note">สัดส่วนต้องรวม 100% — แก้กติกาแล้วระบบสร้างเวอร์ชันใหม่ (ผลการปันส่วนเดิมไม่ถูกลบ)</p>
        <form id="rule-form" class="grid-form">
          <label>ชื่อกติกา<input name="name" placeholder="ค่าเช่าสำนักงาน" required></label>
          <label>วิธี<select name="method">
            <option value="percentage">เปอร์เซ็นต์</option><option value="direct">กำหนดตรง</option>
            <option value="revenue">ตามรายได้</option><option value="orders">ตามจำนวนออเดอร์</option><option value="units">ตามจำนวนชิ้น</option></select></label>
          <label>ปลายทาง A (ประเภท:รหัส:%)<input name="a" placeholder="shop:ร้านA:70" required></label>
          <label>ปลายทาง B (ประเภท:รหัส:%)<input name="b" placeholder="shop:ร้านB:30" required></label>
          <div class="form-actions"><button type="submit">บันทึกกติกา</button><span id="rule-msg" class="muted"></span></div>
        </form>
        <div id="rule-list">กำลังโหลด…</div>
      </section>

      <section class="card"><h3>ผลการปันส่วนล่าสุด</h3><div id="alloc-results">กำลังโหลด…</div></section>
      ${drawerHtml}`,
    ),
  mount(root) {
    if (!getTenant()) return;
    const el = (id: string): HTMLElement => root.querySelector<HTMLElement>(`#${id}`)!;

    const loadPnlMappings = async (): Promise<void> => {
      try {
        const { items } = await api.listPnlMappings();
        el('pnl-map-list').innerHTML = dataTable(
          ['ชนิด', 'ค่าจากต้นทาง', 'หัวข้อ P&L'],
          items.map((m: Row) => ({
            cells: [
              esc(SOURCE_KIND_LABELS[String(m['source_kind'])] ?? m['source_kind']),
              esc(m['source_value']),
              esc(m['pnl_category']),
            ],
          })),
          'ยังไม่มี mapping — รายการที่ยังไม่ผูกจะเข้าคิวตรวจสอบแทนการเดา',
        );
      } catch (e) {
        el('pnl-map-list').innerHTML = errorBox(e);
      }
    };

    const loadTemplates = async (): Promise<void> => {
      try {
        const { items } = await api.listColumnTemplates();
        el('tpl-list').innerHTML = dataTable(
          ['ชื่อ template', 'แหล่งข้อมูล', 'จำนวนคอลัมน์', 'การจับคู่'],
          items.map((t: Row) => {
            const map = (t['column_map'] ?? {}) as Record<string, string>;
            return {
              cells: [
                esc(t['template_name']),
                esc(t['source']),
                esc(Object.keys(map).length),
                esc(
                  Object.entries(map)
                    .map(([k, v]) => `${k}→${v}`)
                    .join(', '),
                ),
              ],
            };
          }),
          'ยังไม่มี template',
        );
      } catch (e) {
        el('tpl-list').innerHTML = errorBox(e);
      }
    };

    const loadResults = async (): Promise<void> => {
      try {
        const { items } = await api.listRules();
        const rows: { cells: string[] }[] = [];
        for (const rule of items.slice(0, 5)) {
          const res = await api.listAllocationResults(String(rule['id']));
          for (const r of res.items) {
            rows.push({
              cells: [
                esc(rule['name']),
                esc(r['rule_version'] ?? '—'),
                esc(r['target_type'] ?? '—'),
                esc(r['target_id'] ?? '—'),
                money(r['amount']),
              ],
            });
          }
        }
        el('alloc-results').innerHTML = dataTable(
          ['กติกา', 'เวอร์ชัน', 'ปลายทาง', 'รหัส', 'ยอดที่ปันส่วน'],
          rows,
          'ยังไม่มีผลการปันส่วน',
        );
      } catch (e) {
        el('alloc-results').innerHTML = errorBox(e);
      }
    };

    const loadRules = async (): Promise<void> => {
      try {
        const { items } = await api.listRules();
        el('rule-list').innerHTML = dataTable(
          ['ชื่อ', 'วิธี', 'เวอร์ชัน', 'สถานะ', ''],
          items.map((r: Row) => ({
            cells: [
              esc(r['name']),
              esc(r['method']),
              esc(r['version']),
              r['is_active'] ? pill('ใช้งาน', 'ok') : pill('เวอร์ชันเก่า'),
              `<button class="mini" data-run="${esc(r['id'])}">ปันส่วนยอด</button>`,
            ],
          })),
          'ยังไม่มีกติกาการปันส่วน',
        );
        el('rule-list')
          .querySelectorAll<HTMLButtonElement>('button[data-run]')
          .forEach((b) =>
            b.addEventListener('click', () => {
              void (async () => {
                const amount = await promptDialog({
                  title: 'ปันส่วนค่าใช้จ่าย',
                  label: 'ยอดที่ต้องการปันส่วน (บาท)',
                  inputMode: 'decimal',
                  confirmText: 'ปันส่วน',
                });
                if (!amount) return;
                api
                  .runAllocation({ ruleId: b.dataset['run']!, amount })
                  .then(loadResults)
                  .catch((e: unknown) => alert(e instanceof Error ? e.message : 'ปันส่วนไม่สำเร็จ'));
              })();
            }),
          );
      } catch (e) {
        el('rule-list').innerHTML = errorBox(e);
      }
    };

    bindSubmit(
      root.querySelector<HTMLFormElement>('#pnl-map-form')!,
      el('pnl-map-msg'),
      async (f) => {
        await api.savePnlMapping({
          sourceKind: val(f, 'sourceKind'),
          sourceValue: val(f, 'sourceValue'),
          pnlCategory: val(f, 'pnlCategory'),
        });
      },
      loadPnlMappings,
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#tpl-form')!,
      el('tpl-msg'),
      async (f) => {
        const raw = val(f, 'columnMap');
        const columnMap: Record<string, string> = {};
        for (const line of raw.split(/\r?\n/)) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          const [col, target] = trimmed.split('=');
          if (!col || !target) throw new Error(`รูปแบบไม่ถูกต้อง: "${trimmed}" (ใช้ ชื่อคอลัมน์=target)`);
          columnMap[col.trim()] = target.trim();
        }
        if (Object.keys(columnMap).length === 0) throw new Error('ยังไม่ได้ระบุการจับคู่คอลัมน์');
        await api.saveColumnTemplate({
          templateName: val(f, 'templateName'),
          source: val(f, 'source'),
          columnMap,
        });
      },
      loadTemplates,
    );

    bindSubmit(
      root.querySelector<HTMLFormElement>('#rule-form')!,
      el('rule-msg'),
      async (f) => {
        const parse = (s: string): Row => {
          const [targetType, targetId, pct] = s.split(':');
          return {
            targetType,
            ...(targetId ? { targetId } : {}),
            ...(pct ? { percentage: Number(pct) } : {}),
          };
        };
        await api.createRule({
          name: val(f, 'name'),
          method: val(f, 'method'),
          targets: [parse(val(f, 'a')), parse(val(f, 'b'))],
        });
      },
      loadRules,
    );

    const loadAliases = async (): Promise<void> => {
      try {
        const [{ items }, skus, shops] = await Promise.all([
          api.listAliases(),
          api.listSkus({ limit: 200 }),
          api.listShops(),
        ]);
        const skuLabel = new Map(skus.items.map((s) => [s.id, s.skuCode]));
        const shopLabel = new Map(shops.items.map((s: Row) => [String(s['id']), String(s['name'])]));
        el('alias-sku').innerHTML = skus.items
          .map((s) => `<option value="${esc(s.id)}">${esc(s.skuCode)}${s.name ? ` — ${esc(s.name)}` : ''}</option>`)
          .join('');
        el('alias-shop').innerHTML = shops.items
          .map((s: Row) => `<option value="${esc(s['id'])}">${esc(s['name'])}</option>`)
          .join('');
        el('alias-list').innerHTML = dataTable(
          ['SKU', 'ช่องทาง', 'ร้าน', 'รหัสจากช่องทาง', 'ชื่อจากช่องทาง'],
          items.map((a: Row) => ({
            cells: [
              esc(skuLabel.get(String(a['sku_id'])) ?? a['sku_id']),
              esc(a['channel_code']),
              esc(shopLabel.get(String(a['shop_id'])) ?? a['shop_id']),
              esc(a['external_sku_id']),
              esc(a['external_name'] ?? '—'),
            ],
          })),
          'ยังไม่มีการผูกรหัสจากช่องทาง',
        );
      } catch (e) {
        el('alias-list').innerHTML = errorBox(e);
      }
    };

    bindSubmit(
      root.querySelector<HTMLFormElement>('#alias-form')!,
      el('alias-msg'),
      async (f) => {
        const skuId = val(f, 'skuId');
        if (!skuId) throw new Error('ยังไม่มี SKU ในกิจการนี้ — เพิ่มสินค้าที่หน้า “สินค้าและต้นทุน SKU” ก่อน');
        const body: Row = {
          skuId,
          shopId: val(f, 'shopId'),
          channelCode: val(f, 'channelCode'),
          externalSkuId: val(f, 'externalSkuId'),
        };
        const name = val(f, 'externalName');
        if (name) body['externalName'] = name;
        await api.createAlias(body);
      },
      loadAliases,
    );

    void loadAliases();
    void loadPnlMappings();
    void loadTemplates();
    void loadRules();
    void loadResults();
  },
};
